import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff, Key } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";

import logo from "../../assets/MariTest_logomark.png";
import dashboardPreview from "../../assets/dashboard-preview.png";

const ResetPasswordPage: React.FC = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  /* ----------------------------------------------------
     Removes page scroll safely
  ---------------------------------------------------- */
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  /* ----------------------------------------------------
     Submit new password
  ---------------------------------------------------- */
  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setMessage("");

    if (!password || !confirm) {
      setError("Both password fields are required.");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters long.");
      return;
    }

    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`/api/auth/reset-password/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });

      const data = await res.json();

      if (res.ok) {
        setMessage("Your password has been reset successfully.");
        setTimeout(() => navigate("/login"), 1500);
      } else {
        setError(data.message || "Unable to reset password.");
      }
    } catch (err) {
      setError("Network error. Please try again.");
    }

    setLoading(false);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        height: "100vh",
        width: "100vw",
        overflow: "hidden",
        bgcolor: "#0b1120",
        position: "fixed",
        inset: 0,
      }}
    >
      {/* LEFT PANEL */}
      <Box
        sx={{
          flex: { xs: "1", md: "0 0 45%" },
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          px: { xs: 4, sm: 6, md: 8 },
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 400 }}>
          <Box sx={{ mb: 6, textAlign: "center" }}>
            <img
              src={logo}
              alt="MariTest Logo"
              style={{
                width: 100,
                marginBottom: "1.5rem",
                display: "block",
                marginLeft: "auto",
                marginRight: "auto",
              }}
            />

            <Typography variant="h5" sx={{ fontWeight: 700, color: "#f9fafb" }}>
              Reset Password
            </Typography>

            <Typography
              variant="body2"
              sx={{ color: "#d1d5db", mt: 1, fontSize: "0.95rem" }}
            >
              Create a strong new password for your account.
            </Typography>
          </Box>

          {/* SUCCESS */}
          {message && (
            <Alert
              severity="success"
              sx={{
                mb: 2,
                backgroundColor: "rgba(16,185,129,0.1)",
                color: "#4ade80",
              }}
            >
              {message}
            </Alert>
          )}

          {/* ERROR */}
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                backgroundColor: "rgba(239,68,68,0.1)",
                color: "#f87171",
              }}
            >
              {error}
            </Alert>
          )}

          {/* FORM */}
          <form onSubmit={handleReset}>
            <TextField
              fullWidth
              label="New Password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{
                mb: 3,
                input: { color: "#f3f4f6" },
                label: { color: "#9ca3af" },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  backgroundColor: "#111827",
                  "& fieldset": { borderColor: "#1f2937" },
                  "&:hover fieldset": { borderColor: "#3b82f6" },
                  "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Key sx={{ color: "#9ca3af" }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <IconButton onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? (
                      <VisibilityOff sx={{ color: "#9ca3af" }} />
                    ) : (
                      <Visibility sx={{ color: "#9ca3af" }} />
                    )}
                  </IconButton>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Confirm Password"
              type={showPassword ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              sx={{
                mb: 3,
                input: { color: "#f3f4f6" },
                label: { color: "#9ca3af" },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  backgroundColor: "#111827",
                  "& fieldset": { borderColor: "#1f2937" },
                  "&:hover fieldset": { borderColor: "#3b82f6" },
                  "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
                },
              }}
            />

            <Button
              fullWidth
              variant="contained"
              type="submit"
              disabled={loading}
              sx={{
                backgroundColor: "#3b82f6",
                py: 1.3,
                fontWeight: 600,
                textTransform: "none",
                borderRadius: "10px",
                boxShadow: "0 4px 15px rgba(59,130,246,0.25)",
                "&:hover": { backgroundColor: "#2563eb" },
              }}
            >
              {loading ? "Resetting..." : "Reset Password"}
            </Button>

            <Typography
              sx={{
                mt: 3,
                textAlign: "center",
                color: "#60a5fa",
                fontWeight: 600,
                cursor: "pointer",
                "&:hover": { color: "#93c5fd" },
              }}
              onClick={() => navigate("/login")}
            >
              Back to Login
            </Typography>
          </form>
        </Box>
      </Box>

      {/* RIGHT PANEL */}
      <Box
        sx={{
          flex: 1,
          display: { xs: "none", md: "flex" },
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
        }}
      >
        <Box
          component="img"
          src={dashboardPreview}
          alt="Dashboard Background"
          sx={{
            position: "absolute",
            width: "100%",
            height: "100%",
            objectFit: "cover",
            filter: "blur(6px) brightness(0.35)",
          }}
        />
      </Box>
    </Box>
  );
};

export default ResetPasswordPage;
